import { SelectedInputType } from 'src/features/trends/types/selected-input';
import { ChartData, SeriesVariant } from 'src/components/streametric/lineChart/types';
import { isDiscreteDataType, isOnChangeTag } from 'src/features/trends/utils';

type ValueType = {
  [timestamp: string]: number;
};
type ReadingType = {
  displayName: string;
  rawInputName: string;
  rawUnit?: string;
  tagId: string;
  values: ValueType;
};

const BAND_VARIANTS: SeriesVariant[] = ['min', 'avg', 'max'];
const VARIANT_LABEL: Record<SeriesVariant, string> = { min: 'Min', avg: 'Avg', max: 'Max' };

const toLocalTimestamp = (date: string | Date): number => {
  const utcDate = new Date(date);
  return utcDate.getTime() - utcDate.getTimezoneOffset() * 60 * 1000;
};

const buildTransformedData = (values: ValueType[]): [number, number][] => {
  const removedEmptyObjects = values.filter((obj) => Object.keys(obj).length !== 0);
  const dataArray: { datetime: string; value: number }[] = [];

  for (const key in removedEmptyObjects) {
    if (Object.hasOwnProperty.call(removedEmptyObjects, key)) {
      const nestedData = removedEmptyObjects[key];
      for (const nestedKey in nestedData) {
        if (Object.hasOwnProperty.call(nestedData, nestedKey)) {
          dataArray.push({ datetime: nestedKey, value: nestedData[nestedKey] });
        }
      }
    }
  }

  return dataArray.map((entry) => [toLocalTimestamp(entry.datetime), entry.value]);
};

// On-change data only reports a point when the value actually changes, so the last
// known value is guaranteed to hold through the end of the queried range. Anchoring
// that value to the window edges turns a lone point into a visible line instead of a
// single dot — or, when the only reading falls outside the window, into a line that
// actually lands inside the pinned x-axis instead of being drawn off-canvas.
//
// startDate/endDate are absolute ISO strings (toISOString(), ends in "Z"), whereas the
// reading timestamps are naive local strings. toLocalTimestamp only cancels the host
// offset for the naive case; running a "Z" string through it would apply a spurious
// timezone shift. Both the data points and the x-axis min/max are in true UTC, so
// compare the window bounds in that same space.
const extendOnChangeData = (
  data: [number, number][],
  startDate?: string,
  endDate?: string
): [number, number][] => {
  if (data.length === 0) return data;

  let result = data;

  // Left edge: the backend may return the latest reading from before startDate (an
  // on-change value in effect at the window start). Left as-is it plots left of the
  // axis min and never shows. Anchor the value in effect at startDate onto the edge so
  // the stepped line enters from the left, dropping the now-redundant off-window points.
  if (startDate) {
    const startTimestamp = new Date(startDate).getTime();
    let priorIndex = -1;
    for (let i = 0; i < result.length && result[i][0] <= startTimestamp; i++) {
      priorIndex = i;
    }
    if (priorIndex !== -1 && result[priorIndex][0] < startTimestamp) {
      result = [[startTimestamp, result[priorIndex][1]], ...result.slice(priorIndex + 1)];
    }
  }

  // Right edge: carry the last known value forward to endDate so the value holds
  // through to the end of the queried range.
  if (endDate) {
    const [lastTimestamp, lastValue] = result[result.length - 1];
    const endTimestamp = new Date(endDate).getTime();
    if (endTimestamp > lastTimestamp) {
      result = [...result, [endTimestamp, lastValue]];
    }
  }

  return result;
};

const buildSeriesName = (siteName: string, inputName: string, unit: string): string => {
  const displayUnit = unit && unit !== 'null' ? `(${unit})` : '';
  return siteName == null
    ? `${inputName} ${displayUnit}`.trim()
    : `${siteName} - ${inputName} ${displayUnit}`.trim();
};

export const getSeries = (
  readings: ReadingType[],
  selectedInputs: SelectedInputType[],
  startDate?: string,
  endDate?: string
): ChartData[] => {
  if (readings.length === 0) return [];

  return selectedInputs.flatMap((x, index): ChartData[] => {
    const baseName = buildSeriesName(x.siteName, x.inputName, x.unit);

    // Matched by row index (not inputId) so duplicate tags with different
    // statistics don't collide with each other's data.
    if (x.statistic === 'Min-Average-Max') {
      return BAND_VARIANTS.map((variant) => {
        const filteredTags = readings?.filter((item) => item.tagId === `${index}__${variant}`);
        const values = filteredTags?.map((item: any) => item.values);
        return {
          name: `${baseName} [${VARIANT_LABEL[variant]}]`,
          data: buildTransformedData(values),
          seriesVariant: variant,
          stepped: isDiscreteDataType(x.dataType),
        };
      });
    }

    const isOnChange = isOnChangeTag(x);

    const filteredTags = readings?.filter((item) => item.tagId === `${index}`);

    const values = filteredTags?.map((item: any) => item.values);
    const data = buildTransformedData(values);

    return [
      {
        name: baseName,
        data: isOnChange ? extendOnChangeData(data, startDate, endDate) : data,
        stepped: isOnChange,
      },
    ];
  });
};
